// DiscountBadge — toont een kortingvinkje op productkaarten.
//
// Logica (matcht ProductDiscountModal preview):
//   1. Liquidatie heeft voorrang als liquidation_active=1 EN huidige tijd
//      binnen [liquidation_start, liquidation_end].
//   2. Anders: standaard discount_pct als show_discount_badge=1.
//   3. Korting nooit > 50% (API blokkeert dit, maar we clampen alsnog).
//
// Resolveert ook prijs-paar (oude prijs doorstreept + nieuwe prijs vet)
// als `catalogPrice` meegegeven wordt.

import { Tag, Flame } from 'lucide-react';
import type { ApiProduct } from '../lib/api';

export interface DiscountInfo {
  active: boolean;
  pct: number;             // 0–50
  label: string;           // displayed label (or fallback)
  isLiquidation: boolean;  // amber if true, rose if false
  endTs: number | null;    // for countdown if liquidation
}

/** Compute the active discount for a product (or null when none). */
export function resolveDiscount(p: Pick<ApiProduct,
  'show_discount_badge' | 'discount_pct' | 'discount_label'
  | 'liquidation_active' | 'liquidation_pct' | 'liquidation_label'
  | 'liquidation_start' | 'liquidation_end'
>): DiscountInfo | null {
  const now = Math.floor(Date.now() / 1000);

  // Check liquidation first (overrides regular discount when in window)
  if (p.liquidation_active && (p.liquidation_pct ?? 0) > 0) {
    const startOk = !p.liquidation_start || p.liquidation_start <= now;
    const endOk = !p.liquidation_end || p.liquidation_end >= now;
    if (startOk && endOk) {
      return {
        active: true,
        pct: Math.min(50, Math.max(0, p.liquidation_pct ?? 0)),
        label: p.liquidation_label || 'Liquidatie',
        isLiquidation: true,
        endTs: p.liquidation_end ?? null,
      };
    }
  }

  // Fallback: regular discount badge
  if (p.show_discount_badge && (p.discount_pct ?? 0) > 0) {
    return {
      active: true,
      pct: Math.min(50, Math.max(0, p.discount_pct ?? 0)),
      label: p.discount_label || 'Korting',
      isLiquidation: false,
      endTs: null,
    };
  }

  return null;
}

/** Days remaining until end of liquidation window (rounded up, min 0). */
export function daysRemaining(endTs: number | null): number | null {
  if (!endTs) return null;
  const now = Math.floor(Date.now() / 1000);
  if (endTs < now) return 0;
  return Math.ceil((endTs - now) / 86400);
}

/**
 * Compact corner badge (for product cards in grid).
 * Use as absolutely-positioned overlay on the product image.
 */
export function DiscountBadge({ product, size = 'md' }: {
  product: Pick<ApiProduct,
    'show_discount_badge' | 'discount_pct' | 'discount_label'
    | 'liquidation_active' | 'liquidation_pct' | 'liquidation_label'
    | 'liquidation_start' | 'liquidation_end'
  >;
  size?: 'sm' | 'md' | 'lg';
}) {
  const info = resolveDiscount(product);
  if (!info) return null;

  const Icon = info.isLiquidation ? Flame : Tag;
  const colorCls = info.isLiquidation
    ? 'bg-gradient-to-br from-amber-500 to-rose-600 text-white shadow-amber-500/40'
    : 'bg-gradient-to-br from-rose-500 to-rose-700 text-white shadow-rose-500/40';

  const sizeCls = size === 'sm'
    ? 'text-[10px] px-2 py-0.5 gap-0.5'
    : size === 'lg'
      ? 'text-sm px-3.5 py-1.5 gap-1.5'
      : 'text-xs px-3 py-1 gap-1';
  const iconSize = size === 'sm' ? 10 : size === 'lg' ? 14 : 12;

  return (
    <span
      className={`inline-flex items-center font-extrabold rounded-full shadow-lg ${colorCls} ${sizeCls}`}
      title={info.label}
    >
      <Icon size={iconSize} className={info.isLiquidation ? 'animate-pulse' : ''} />
      −{info.pct}%
    </span>
  );
}

/**
 * Discount price block (old price strikethrough + new price highlighted).
 * Returns just the catalog price if no discount is active.
 */
export function DiscountPrice({
  product,
  catalogPrice,
  unit = '/m²',
  className = '',
}: {
  product: Pick<ApiProduct,
    'show_discount_badge' | 'discount_pct' | 'discount_label'
    | 'liquidation_active' | 'liquidation_pct' | 'liquidation_label'
    | 'liquidation_start' | 'liquidation_end'
  >;
  catalogPrice: number;
  unit?: string;
  className?: string;
}) {
  const info = resolveDiscount(product);
  if (!info) {
    return (
      <span className={`font-bold text-[#1a1a1a] ${className}`}>
        €{catalogPrice.toFixed(2)}{unit}
      </span>
    );
  }
  const newPrice = Math.round(catalogPrice * (1 - info.pct / 100) * 100) / 100;
  const colorCls = info.isLiquidation ? 'text-amber-700' : 'text-rose-700';

  return (
    <span className={`inline-flex items-baseline gap-2 ${className}`}>
      <span className="text-sm line-through text-[#8a7e74]">€{catalogPrice.toFixed(2)}</span>
      <span className={`font-extrabold ${colorCls}`}>€{newPrice.toFixed(2)}{unit}</span>
    </span>
  );
}

/**
 * Marketing strip — shown above the price block to motivate purchase.
 * "Bespaar €123 op deze steensoort!"
 */
export function DiscountSavingsStrip({
  product,
  catalogPrice,
  surfaceM2 = 1,
  className = '',
  savingsLabel = 'bespaar',
}: {
  product: Pick<ApiProduct,
    'show_discount_badge' | 'discount_pct' | 'discount_label'
    | 'liquidation_active' | 'liquidation_pct' | 'liquidation_label'
    | 'liquidation_start' | 'liquidation_end'
  >;
  catalogPrice: number;
  surfaceM2?: number;
  className?: string;
  /** v3.2.102 — geïnjecteerd door de parent (t('home.savings')) zodat de
   *  strip in de gekozen taal verschijnt. Default NL voor backwards-compat. */
  savingsLabel?: string;
}) {
  const info = resolveDiscount(product);
  if (!info) return null;

  const savings = catalogPrice * surfaceM2 * (info.pct / 100);
  const days = daysRemaining(info.endTs);
  const Icon = info.isLiquidation ? Flame : Tag;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold ${
      info.isLiquidation
        ? 'bg-amber-100 text-amber-800 border border-amber-300'
        : 'bg-rose-100 text-rose-800 border border-rose-300'
    } ${className}`}>
      <Icon size={14} className={info.isLiquidation ? 'animate-pulse' : ''} />
      <span>
        {info.label}: <strong>−{info.pct}%</strong>
        {surfaceM2 > 1 && ` · ${savingsLabel} €${savings.toFixed(2)}`}
        {days != null && days <= 30 && (
          <span className="ml-1.5 opacity-80">
            (nog {days === 0 ? '<1 dag' : days === 1 ? '1 dag' : `${days} dagen`})
          </span>
        )}
      </span>
    </div>
  );
}
